import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Card,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import MainContainer from "../../../layout/MainContainer";
import { Loader, showError, showToast } from "../../../constants";
import { GetCategories, AddCategory, UpdateCategory } from "../../../services/home";
import { PasswordModal } from "../../../components";

interface Category {
  _id: string;
  name: string;
  description?: string;
  parentCategoryId?: string | null;
}

const CategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);
  const existingCategory = location.state?.item as Category | undefined;

  const [loading, setLoading] = useState(false);
  const [parentCategories, setParentCategories] = useState<Category[]>([]);
  const [openPassword, setOpenPassword] = useState(false);

  const fetchParentCategories = async () => {
    try {
      const response = (await GetCategories({ parentCategoryId: "null" })) as {
        code: number;
        data: Category[];
      };
      if (response?.code === 200) {
        setParentCategories(response.data || []);
      }
    } catch (error) {
      showError("Failed to load parent categories");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: {
      name: existingCategory?.name ?? "",
      description: existingCategory?.description ?? "",
      parentCategoryId: existingCategory?.parentCategoryId ?? "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required").max(30, "Max 30 characters"),
      description: Yup.string().required("Description is required").max(50, "Max 50 characters"),
      parentCategoryId: Yup.string().nullable(),
    }),
    onSubmit: (_values, { setSubmitting }) => {
      setSubmitting(false);
      setOpenPassword(true);
    },
  });

  const handleSubmitWithPassword = async (password: string) => {
    setLoading(true);
    try {
      const payload = {
        name: formik.values.name,
        description: formik.values.description || undefined,
        parentCategoryId: formik.values.parentCategoryId || null,
        categoryPassword: password,
      };
      const response = (await (isEdit
        ? UpdateCategory(id!, payload)
        : AddCategory(payload))) as { code: number };
      if (response?.code === 200 || response?.code === 201) {
        showToast(`Category ${isEdit ? "updated" : "created"} successfully`);
        setOpenPassword(false);
        navigate("/manage-categories");
      }
    } catch (error) {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParentCategories();
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{isEdit ? "Edit Category" : "Add Category"}</h1>
        </div>
        <Card className="cards" sx={{ p: 3 }}>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={
                (formik.touched.name && formik.errors.name) ||
                `${formik.values.name.length}/30`
              }
              inputProps={{ maxLength: 30 }}
              sx={{ mb: 2.5 }}
            />

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={
                (formik.touched.description && formik.errors.description) ||
                `${formik.values.description.length}/50`
              }
              inputProps={{ maxLength: 50 }}
              multiline
              rows={2}
              sx={{ mb: 2.5 }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Parent Category (leave blank to create a parent)</InputLabel>
              <Select
                name="parentCategoryId"
                value={formik.values.parentCategoryId ?? ""}
                label="Parent Category (leave blank to create a parent)"
                onChange={formik.handleChange}
              >
                <MenuItem value="">None (top-level category)</MenuItem>
                {parentCategories
                  .filter((cat) => !isEdit || cat._id !== id)
                  .map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
              </Select>
              {formik.touched.parentCategoryId && formik.errors.parentCategoryId && (
                <FormHelperText error>{formik.errors.parentCategoryId}</FormHelperText>
              )}
            </FormControl>

            <Button
              type="submit"
              className="btn btn_primary"
              disabled={formik.isSubmitting}
            >
              {isEdit ? "Update" : "Create"}
            </Button>
            <Button
              className="btn btn_secondary"
              onClick={() => navigate(-1)}
              sx={{ ml: 1.5 }}
            >
              Cancel
            </Button>
          </form>
        </Card>
      </div>
      <PasswordModal
        open={openPassword}
        onClose={() => setOpenPassword(false)}
        onConfirm={handleSubmitWithPassword}
        title="Enter Category Password"
        description={`${isEdit ? "Updating" : "Creating"} a category requires the category protection password.`}
        confirmLabel={isEdit ? "Update" : "Create"}
        loading={loading}
      />
    </MainContainer>
  );
};

export default CategoryForm;
